import { useCreateBalanceOperation } from '../../balanceOperation/create/hooks'
import React, { ChangeEvent, useCallback, useEffect, useMemo } from 'react'
import { debounce } from 'lodash'
import {
  accountsMounted,
  createBalanceOperation,
  setBalanceOperation,
  submitCreateBalanceOperation,
  walletsMounted,
} from '../../balanceOperation/create/model'
import {
  CButton,
  CCard,
  CCardBody,
  CCardHeader,
  CCol,
  CForm,
  CFormInput,
  CFormSelect,
  CFormTextarea,
  CRow,
  CTabContent,
} from '@coreui/react-pro'
import { ApiResult } from '../../../../components/apiResult/ApiResult'
import { Guard } from '../../../../features/session/guard'

export const CreateBalanceOperationForm: React.FC = () => {
  const {
    balanceOperation,
    accounts,
    wallets,
    createBalanceOperationLoading,
    accountsLoading,
    walletsLoading,
  } = useCreateBalanceOperation()

  const debounceLoadData = useCallback(
    debounce((params) => {
      if (balanceOperation.sourceType === 'account') {
        accountsMounted(params)
      } else if (balanceOperation.sourceType === 'wallet') {
        walletsMounted(params)
      }
    }, 1000),
    [balanceOperation.sourceType],
  )

  useEffect(() => {
    if (balanceOperation.clientId) {
      if (balanceOperation.sourceType === 'account') {
        accountsMounted({ id: balanceOperation.clientId })
      } else if (balanceOperation.sourceType === 'wallet') {
        walletsMounted({ id: balanceOperation.clientId })
      }
    }
  }, [balanceOperation.sourceType, balanceOperation.clientId])

  useEffect(() => {
    setBalanceOperation({
      ...balanceOperation,
      sourceId: '',
    })
  }, [accountsLoading, walletsLoading, balanceOperation.sourceType])

  const handleChange = (
    event: ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    setBalanceOperation({
      ...balanceOperation,
      [event.currentTarget.name]: event.currentTarget.value,
    })
    if (
      event.currentTarget.name === 'clientId' &&
      event.currentTarget.value &&
      balanceOperation.sourceType !== 'nova'
    ) {
      debounceLoadData({ id: event.currentTarget.value })
    }
  }

  const isDisplaySourceId = useMemo(
    () =>
      balanceOperation.clientId &&
      balanceOperation.sourceType !== 'nova' &&
      (balanceOperation.sourceType === 'account'
        ? accounts.length !== 0
        : wallets.length !== 0),
    [balanceOperation.clientId, balanceOperation.sourceType, accounts, wallets],
  )

  return (
    <CTabContent className="pt-3">
      <CForm>
        <CRow className="mb-3">
          <CCol sm={3}>
            <CFormInput
              type="number"
              id="clientId"
              name="clientId"
              value={balanceOperation.clientId}
              onChange={handleChange}
              label="Client ID"
            />
          </CCol>
        </CRow>
        <CRow className="mb-3">
          <CCol sm={3}>
            <CFormSelect
              id="sourceType"
              name="sourceType"
              value={balanceOperation.sourceType}
              onChange={handleChange}
              label={'Source type'}
            >
              <option>Choose source type</option>
              <option value="account">Account</option>
              <option value="wallet">Wallet</option>
              <option value="nova">Nova</option>
              <Guard permission="create_balance_operation_ib">
                <option value="partner_wallet">Partner Wallet</option>
              </Guard>
            </CFormSelect>
          </CCol>
          {isDisplaySourceId && (
            <CCol sm={3}>
              <CFormSelect
                id="sourceId"
                name="sourceId"
                value={balanceOperation.sourceId}
                disabled={accountsLoading || walletsLoading}
                onChange={handleChange}
                label={'Source Id'}
              >
                <option>Choose source id</option>
                {balanceOperation.sourceType === 'account'
                  ? accounts
                      .filter((account) => !account.isDemo)
                      .map((account) => (
                        <option value={account.accountId}>
                          {account.accountName}
                        </option>
                      ))
                  : wallets.map((account) => (
                      <option value={account.walletId}>
                        {account.walletId}
                      </option>
                    ))}
              </CFormSelect>
            </CCol>
          )}
        </CRow>
        <CRow className="mb-3">
          <CCol sm={3}>
            <CFormInput
              type="text"
              id="amount"
              name="amount"
              value={balanceOperation.amount}
              onChange={handleChange}
              label="Amount"
            />
          </CCol>
        </CRow>
        <CRow className="mb-3">
          <CCol sm={3}>
            <CFormTextarea
              id="comment"
              name="comment"
              value={balanceOperation.comment}
              onChange={handleChange}
              label="Comment"
            />
          </CCol>
          <CCol sm={3}>
            <CFormTextarea
              id="internalComment"
              name="internalComment"
              value={balanceOperation.internalComment}
              onChange={handleChange}
              label="Internal comment"
            />
          </CCol>
        </CRow>
        <CRow className="mb-3">
          <CCol sm={6} className="d-flex justify-content-end">
            <CButton
              className="mb-3"
              disabled={createBalanceOperationLoading}
              onClick={() => {
                submitCreateBalanceOperation({
                  ...balanceOperation,
                  clientId: Number(balanceOperation.clientId),
                  sourceId:
                    balanceOperation.sourceType !== 'nova'
                      ? Number(balanceOperation.sourceId)
                      : Number(balanceOperation.clientId),
                })
              }}
            >
              Save
            </CButton>
          </CCol>
        </CRow>

        <ApiResult
          effect={createBalanceOperation}
          successMessage="Balance operation successfully created"
        />
      </CForm>
    </CTabContent>
  )
}
